import React from 'react';
import { IconCheckmarkFill } from '@saucelabs/ds-icons';
import { Typography } from '@saucelabs/ds-typography/lib';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { ROUTE_PARENT_SECTION, ROUTE_SECTION_CHECKOUT, ROUTE_SECTION_DETAILS, ROUTE_SECTION_PURCHASE } from '../../constants';

const STEPS_NUM = 3;

const CompletedCheckoutSymbol = () => (
  <div className="flex items-center justify-center w-4 h-4 rounded-full bg-blue-700 text-white mx-0.5">
    <IconCheckmarkFill className="flex fill-current w-2" />
  </div>
);

interface UncompletedCheckoutSymbolProps {
  furtherStep: boolean;
  stepNumber: number;
}

const UncompletedCheckoutSymbol: React.FC<UncompletedCheckoutSymbolProps> = ({
  furtherStep, stepNumber,
}) => {
  const tailwindDivClasses = `flex items-center justify-center w-4 h-4 rounded-full border-solid mx-0.5 ${furtherStep ? 'bg-gray-50 text-gray-700 border-gray-700' : 'bg-blue-50 text-blue-700 border-blue-700'}`;
  const tailwindTypographyClasses = `${furtherStep ? 'text-gray-700' : 'text-blue-700 font-semibold'}`;
  return (
    <div className={tailwindDivClasses}>
      <Typography
        className={tailwindTypographyClasses}
        disableSpacing
        variant="body-large"
      >
        { stepNumber }
      </Typography>
    </div>
  );
};

interface CheckoutSymbolProps {
  currentStep: number;
  stepNumber: number;
  link?: string;
}

const CheckoutSymbol = ({ currentStep, stepNumber, link }): React.FC<CheckoutSymbolProps> => {
  const dispatch = useDispatch();

  let symbolComponent;
  if (currentStep > stepNumber) {
    symbolComponent = <CompletedCheckoutSymbol />;
  } else {
    symbolComponent = (
      <UncompletedCheckoutSymbol
        furtherStep={currentStep < stepNumber}
        stepNumber={stepNumber}
      />
    );
  }

  return (
    <Link to={link}>
      { symbolComponent }
    </Link>
  );
};

interface CheckoutStepSeparatorProps {
  fromStep: number;
  currentStep: number;
}

const CheckoutStepSeparator: React.FC<CheckoutStepSeparatorProps> = ({ fromStep, currentStep }) => (
  <div className={`flex items-center justify-center w-9 h-px ${currentStep + 1 > fromStep ? 'bg-blue-700' : 'bg-gray-300'}`} />
);

interface CheckoutStepLabelProps {
  stepLabel: string;
  furtherStep: boolean;
}

const CheckoutStepLabel: React.FC<CheckoutStepLabelProps> = ({ stepLabel, furtherStep }) => {
  const tailwindClasses = `${furtherStep ? 'text-gray-700' : 'text-blue-700 font-semibold'}`;
  return (
    <Typography
      className={tailwindClasses}
      disableSpacing
      variant="body-large"
    >
      {stepLabel}
    </Typography>
  );
};

interface CheckoutStepProps {
  currentStep: number;
  stepLabel: string;
  stepNumber: number;
  link: string;
}

const CheckoutStep: React.FC<CheckoutStepProps> = ({
  currentStep, stepLabel, stepNumber, link,
}) => (
  <div className="flex flex-col justify-center">
    <div className="flex items-center">
      {stepNumber === 1 ?
        <div className="w-9" /> : (
          <CheckoutStepSeparator
            currentStep={currentStep}
            fromStep={stepNumber}
          />
        )}
      <CheckoutSymbol
        currentStep={currentStep}
        link={link}
        stepNumber={stepNumber}
      />
      {stepNumber === STEPS_NUM ?
        <div className="w-9" /> : (
          <CheckoutStepSeparator
            currentStep={currentStep}
            fromStep={stepNumber + 1}
          />
        )}
    </div>
    <div className="flex justify-center mt-1">
      <CheckoutStepLabel
        furtherStep={currentStep < stepNumber}
        stepLabel={stepLabel}
      />
    </div>
  </div>
);

interface TwoStepsCheckoutProgressProps {
  checkoutStep: number;
}

export const TwoStepsCheckoutProgress: React.FC<TwoStepsCheckoutProgressProps> = ({
  checkoutStep,
}) => {
  const detailsLocation = {
    pathname: `/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_DETAILS}`,
    state: {
      companyDetails: true,
      paymentDetails: false,
    },
  };
  return (
    <div className="flex items-center justify-center m-3">
      <CheckoutStep
        currentStep={checkoutStep}
        link={`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_PURCHASE}`}
        stepLabel="Select Plan"
        stepNumber={1}
      />
      <CheckoutStep
        currentStep={checkoutStep}
        link={detailsLocation}
        stepLabel="Enter Details"
        stepNumber={2}
      />
      <CheckoutStep
        currentStep={checkoutStep}
        link={`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_CHECKOUT}`}
        stepLabel="Review and Purchase"
        stepNumber={3}
      />
    </div>
  );
};
